import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { mockDataUSA } from '../mockUSA';
import { usePageTracking, trackFormSubmit, trackSignupStart } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USA = () => {
  const location = useLocation();
  
  // Track page view
  usePageTracking('/english');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupFormData, setSignupFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });

  useEffect(() => {
    if (location.state?.scrollToForm) {
      setTimeout(() => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, agreedToPrivacy: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreedToPrivacy) {
      toast.error('You must accept the privacy policy.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        agreed_to_privacy: formData.agreedToPrivacy,
        source: 'english'
      });

      trackFormSubmit('/english', 'contact');
      toast.success('Thank you! Your form has been submitted successfully. We will contact you soon.');
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error.response?.data?.detail || 'An error occurred. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookCall = () => {
    window.open('https://calendly.com/electrumprosperitygroup/30min', '_blank');
  };

  const handleJoinNow = () => {
    setShowSignupModal(true);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupFormData.agreedToPrivacy) {
      toast.error('You must accept the privacy policy.');
      return;
    }

    if (!signupFormData.name || !signupFormData.email) {
      toast.error('Please fill in your name and email.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/`, {
        name: signupFormData.name,
        email: signupFormData.email,
        phone: signupFormData.phone,
        agreed_to_privacy: signupFormData.agreedToPrivacy,
        source: 'english-signup'
      });

      trackFormSubmit('/english', 'signup');
      trackSignupStart('/english');

      const response = await axios.get(`${API}/join-links/next-rotation`);
      const joinLink = response.data.join_now_link;
      
      toast.success('Thank you! You will be redirected to the signup page.');
      
      setShowSignupModal(false);
      setSignupFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });

      setTimeout(() => {
        window.open(joinLink, '_blank');
      }, 500);

    } catch (error) {
      console.error('Signup form error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="/epg_logo.png" alt="EPG Logo" className="logo-icon" />
          </div>
          <div className="header-buttons">
            <Button variant="outline" className="header-btn" onClick={handleBookCall}>
              Book a Call
            </Button>
            <Button variant="outline" className="header-btn" onClick={() => window.open('/privacy', '_blank')}>
              Privacy
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">{mockDataUSA.hero.badge}</div>
            <h1 className="hero-headline">{mockDataUSA.hero.headline}</h1>
            <p className="hero-subheading">{mockDataUSA.hero.subheading}</p>
            
            <div className="benefits-list">
              {mockDataUSA.hero.benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <Check className="check-icon" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="stats-grid">
              {mockDataUSA.hero.stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                  {stat.sublabel && <div className="stat-sublabel">{stat.sublabel}</div>}
                </div>
              ))}
            </div>

            <div className="hero-ctas">
              <Button className="cta-primary" onClick={handleBookCall}>
                {mockDataUSA.hero.ctas[0].text}
              </Button>
              <Button variant="outline" className="cta-secondary" onClick={handleJoinNow}>
                {mockDataUSA.hero.ctas[1].text}
              </Button>
            </div>

            <div className="hero-email">
              Contact: <a href={`mailto:${mockDataUSA.hero.email}`}>{mockDataUSA.hero.email}</a>
            </div>
          </div>

          <div className="hero-video">
            <div 
              className="video-placeholder clickable-video" 
              onClick={() => window.open('https://www.7kmetals.com/stusimonsen/share/lb99z?preview=true', '_blank')}
            >
              <img 
                src="/epg_logo.png" 
                alt="EPG Logo" 
                className="video-logo"
              />
              <div className="video-overlay">
                <div className="play-button">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="#000">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                </div>
                <span className="video-text">Click to Watch Video</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Videos Section */}
      <section className="main-videos-section">
        <div className="main-videos-container">
          <div className="video-item">
            <iframe 
              title="vimeo-player" 
              src="https://player.vimeo.com/video/1086923842?h=598719970a" 
              width="640" 
              height="360" 
              frameBorder="0" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
              allowFullScreen
              className="video-player-large"
            ></iframe>
            <p className="video-title-large">Wealth Growth Strategies</p>
          </div>
          <div className="video-item">
            <video controls className="video-player-large" poster="/accelerating_wealth_thumbnail.jpg">
              <source src="https://customer-assets.emergentagent.com/job_electrum-wealth/artifacts/zglrlc6y_2025%20Acclerating%20Wealth%20Romanian%20Final.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <p className="video-title-large">Accelerating Wealth Growth</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          {mockDataUSA.features.map((feature, index) => (
            <Card key={index} className="feature-card">
              <CardContent className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-section" id="contact-form">
        <div className="contact-container">
          <h2 className="contact-title">{mockDataUSA.contactForm.title}</h2>
          <form onSubmit={handleSubmit} className="contact-form">
            <Input
              type="text"
              name="name"
              placeholder={mockDataUSA.contactForm.fields.name}
              value={formData.name}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="email"
              name="email"
              placeholder={mockDataUSA.contactForm.fields.email}
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="tel"
              name="phone"
              placeholder={mockDataUSA.contactForm.fields.phone}
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Button type="submit" className="form-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : mockDataUSA.contactForm.submitButton}
            </Button>
            <div className="privacy-checkbox">
              <Checkbox
                id="privacy"
                checked={formData.agreedToPrivacy}
                onCheckedChange={handleCheckboxChange}
                disabled={isSubmitting}
              />
              <label htmlFor="privacy" className="privacy-label">
                {mockDataUSA.contactForm.privacyText}{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="privacy-link">
                  {mockDataUSA.contactForm.privacyLink}
                </a>.
              </label>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-cta">
            <span>Ready to start your journey with EPG?</span>
            <Button className="footer-btn" onClick={handleBookCall}>
              Book a Call
            </Button>
            <Button className="footer-btn-secondary" onClick={handleJoinNow}>
              Join Now!
            </Button>
          </div>
          <div className="footer-info">
            <span>{mockDataUSA.footer.copyright}</span>
            <span>•</span>
            <a href={`mailto:${mockDataUSA.footer.email}`}>{mockDataUSA.footer.email}</a>
          </div>
        </div>
      </footer>

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="signup-modal-overlay" onClick={() => setShowSignupModal(false)}>
          <div className="signup-modal" onClick={(e) => e.stopPropagation()}>
            <button className="signup-modal-close" onClick={() => setShowSignupModal(false)}>
              <X size={24} />
            </button>
            <h2 className="signup-modal-title">Sign Up</h2>
            <p className="signup-modal-subtitle">Complete the form to continue to the signup page</p>
            
            <form onSubmit={handleSignupSubmit} className="signup-modal-form">
              <div className="signup-form-field">
                <Input
                  type="text"
                  id="signup_name_usa"
                  placeholder="Full Name *"
                  value={signupFormData.name}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="email"
                  id="signup_email_usa"
                  placeholder="Email *"
                  value={signupFormData.email}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="tel"
                  id="signup_phone_usa"
                  placeholder="Phone"
                  value={signupFormData.phone}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="signup-input"
                />
              </div>
              <div className="signup-checkbox-container">
                <Checkbox
                  id="signup-privacy-usa"
                  checked={signupFormData.agreedToPrivacy}
                  onCheckedChange={(checked) => setSignupFormData(prev => ({ ...prev, agreedToPrivacy: checked }))}
                />
                <label htmlFor="signup-privacy-usa" className="signup-checkbox-label">
                  I accept the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </label>
              </div>
              <Button 
                type="submit" 
                className="signup-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Next'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default USA;
